// Inventory: Sold pieces (Starchive-Inventory-Section-Plan, section 3.5; the canvas's I6 board). Every piece sold since
// go-live with its photo, whose it was and what it cost. Marking an owner here writes the same record as the Owners tab AND
// fills the sale line, so the Month close, the partner settlement and Polaris change at once. A piece that sold with no cost
// on record can take one here (Tim, 30 Sep: "add the cost to the most recent things that sold"): Add cost writes it on the
// sale line (reports-read line_cost_set, owner ID, audited); Shopify's unit cost and the floor are not touched.
import { useMemo, useState } from 'react'
import { Screen } from '../ReportsApp'
import { downloadCsv, reportsAction, useReport } from '../api'
import { dayShort, int, money, n } from '../format'
import { AsyncButton, Badge, Tile } from '../ui'
import { CostChip, OwnerChip, PhotoImg } from './inventory-ui'
import { PieceDrawer } from './PieceDrawer'
import { useOwnerSigner } from './owner-edit'
import { JoinSale, joinCandidates, sameCustomer } from './JoinSale'

interface SoldLine {
  line_id: string; product_id: string | null; title: string; brand: string; image_url: string | null
  price: number; cost: number | null; cost_source: string; owner: string | null; consignor_key: string | null; split_pct: number | null
  profit: number | null; fee: number | null; refunded: number | null; no_piece: boolean; zero_line: boolean
  sale_no: number | null; order_name: string | null; paid_at: string; method: string; source: string
  cost_by?: string; cost_at?: string
  sale_id: string; customer: string; sale_lines: number; has_order: boolean; shipping: number; tax: number; sale_total: number
  payments: { id: string; method: string; amount: number; order: string | null }[]
}
interface SoldData {
  go_live: string
  tiles: { sold: number; sold_sum: number; no_owner: number; no_owner_sum: number; real_cost: number; profit_sum: number; added?: number; legacy: number; no_piece: number; no_piece_sum: number; zero_lines: number; refunded: number }
  lines: SoldLine[]
  consignors: { id: string; key: string; name: string; default_split: number | null }[]
}

const OWNERS = ['shane', 'masih', 'split', 'consignor'] as const
const label = (o: string) => o[0].toUpperCase() + o.slice(1)

export function Sold() {
  const { data: d, error, loading, refresh } = useReport<SoldData>('inv_sold')
  // the owner ID is the tab's shared one, so the piece drawer opened from here is already signed
  const sg = useOwnerSigner()
  const { ownerId, setOwnerId, signer } = sg
  const [show, setShow] = useState<'all' | 'no_owner' | 'look' | 'legacy'>('all')
  const [marking, setMarking] = useState<string | null>(null)   // line_id with the owner buttons open
  const [consignorId, setConsignorId] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const [msg, setMsg] = useState('')
  const [costing, setCosting] = useState<{ id: string; v: string } | null>(null)   // the sale line with the cost box open
  const [joining, setJoining] = useState<SoldLine | null>(null)   // a Shopify payment being joined into its counter sale
  const [done, setDone] = useState('')

  const rows = useMemo(() => {
    const l = d?.lines ?? []
    if (show === 'no_owner') return l.filter((x) => !x.owner && !x.no_piece)
    if (show === 'look') return l.filter((x) => x.no_piece || x.zero_line || x.refunded != null)
    if (show === 'legacy') return l.filter((x) => !x.no_piece && x.cost == null && x.owner !== 'consignor')
    return l
  }, [d, show])

  const mark = async (line: SoldLine, owner: string) => {
    setMsg('')
    const body: Record<string, unknown> = { product_id: line.product_id, owner, employee_id: ownerId }
    if (owner === 'consignor') {
      if (!consignorId) { setMsg('Pick the consignor first'); return }
      body.consignor_id = consignorId
      const c = (d?.consignors ?? []).find((x) => x.id === consignorId)
      if (c?.default_split != null) body.split_pct = n(c.default_split)
    }
    try { await reportsAction('owner_mark', body); setMarking(null); refresh() } catch (e) { setMsg(`${line.title}: ${(e as Error).message}`) }
  }
  const canCost = (l: SoldLine) => !l.no_piece && l.owner !== 'consignor' && (l.cost == null || l.cost_source === 'added')
  const saveCost = async (l: SoldLine, v: string | null) => {
    setMsg('')
    const cost = v == null ? null : Number(v.replace(/[^0-9.]/g, ''))
    if (v != null && !(v.trim() && Number.isFinite(cost) && (cost as number) >= 0)) { setMsg(`${l.title}: type what it cost in dollars`); return }
    const go = async () => {
      try { await reportsAction('line_cost_set', { employee_id: sg.idRef.current, line_id: l.line_id, cost }); setCosting(null); refresh() } catch (e) { setMsg(`${l.title}: ${(e as Error).message}`) }
    }
    // no owner ID yet: the prompt asks for it and the save carries on once it is typed
    if (signer) await go(); else sg.signed(() => { void go() })
  }
  const costCell = (l: SoldLine) => {
    if (l.no_piece) return ''
    if (costing?.id === l.line_id) return (
      <span className="rp-costbox">
        <input className="rp-costin" inputMode="decimal" autoFocus placeholder="$ cost" value={costing.v} aria-label={`What ${l.title} cost`}
          onChange={(e) => setCosting({ id: l.line_id, v: e.target.value })} onKeyDown={(e) => { if (e.key === 'Escape') setCosting(null); if (e.key === 'Enter') void saveCost(l, costing.v) }} />
        <AsyncButton small run={() => saveCost(l, costing.v)}>Save</AsyncButton>
        {l.cost_source === 'added' && <AsyncButton small run={() => saveCost(l, null)}>Remove</AsyncButton>}
        <button type="button" className="rp-btn rp-btn--small" onClick={() => setCosting(null)} aria-label="Cancel">×</button>
      </span>
    )
    if (l.cost != null && l.cost_source === 'added') return (
      <span className="rp-costbox"><Badge tone="green">Cost {money(l.cost, true)} · added</Badge>
        <button type="button" className="rp-linkish" onClick={() => setCosting({ id: l.line_id, v: String(l.cost) })}>Change</button></span>
    )
    if (l.cost != null) return <Badge tone="green">Cost {money(l.cost, true)}</Badge>
    if (canCost(l)) return <button type="button" className="rp-btn rp-btn--small" title="Type what it cost, if you know it" onClick={() => setCosting({ id: l.line_id, v: '' })}>Add cost</button>
    return <CostChip source={l.owner === 'consignor' ? 'consignor' : 'legacy'} cost={null} />
  }
  // a Shopify order that may be the invoice half of a counter sale: a custom line, or a counter sale for the same customer nearby
  const joinable = (l: SoldLine) => {
    if (!(l.source === 'web' || l.source === 'pos') || !l.has_order || l.sale_lines !== 1 || l.payments.length !== 1 || l.payments[0].method !== 'card') return null
    const c = joinCandidates(l, d?.lines ?? [])
    const same = c.find((x) => sameCustomer(x, l))
    return same ? `Join to #${String(same.sale_no ?? '').padStart(4, '0')}?` : l.no_piece && c.length ? 'Part of a sale?' : null
  }
  const t = d?.tiles
  const saleCell = (l: SoldLine) => l.sale_no ? `#${String(l.sale_no).padStart(4, '0')} · ${l.method}` : `${l.order_name ?? 'Order'} · ${l.source === 'pos' ? 'POS' : 'web'}`
  const cols = 'minmax(0, 2.1fr) 150px 150px 76px 200px 80px 190px'

  return (
    <Screen title="Sold pieces" sub={d ? `Every piece sold since go-live (${dayShort(d.go_live)}), with its photo, whose it was and what it cost` : 'Every piece sold since go-live'}
      right={<>
        <label className="rp-ownerbar"><span>Owner ID</span>
          <input inputMode="numeric" autoComplete="off" value={ownerId} onChange={(e) => setOwnerId(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="••••" aria-label="Owner ID" />
          {ownerId.length === 4 && <Badge tone={signer ? 'green' : 'red'}>{signer ? signer.name : 'Not an owner'}</Badge>}
        </label>
        <AsyncButton run={() => downloadCsv('sales', { from: d?.go_live })} doneLabel="Downloaded">Export CSV</AsyncButton>
      </>} wide>
      {error && <div className="rp-error">{error}</div>}
      {msg && <div className="rp-error">{msg}</div>}
      {done && <div className="rp-okbar">{done}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Sold since go-live" value={t ? int(t.sold) : '…'} sub={t ? `${money(t.sold_sum, true)} paid for them` : ''} />
        <Tile label="Owner not recorded" tone={t && t.no_owner ? 'red' : 'plain'} value={t ? int(t.no_owner) : '…'} sub={t ? `${money(t.no_owner_sum, true)} of sales: nobody is owed on these until marked` : ''} />
        <Tile label="Cost known" tone="green" value={t ? int(t.real_cost) : '…'} sub={t ? `Profit known: ${money(t.profit_sum, true)}.${t.added ? ` ${int(t.added)} added after the sale.` : ''} ${int(t.legacy)} sold with no cost yet: add it where you know it` : ''} />
        <Tile label="No piece behind the sale" tone={t && t.no_piece ? 'red' : 'plain'} value={t ? int(t.no_piece) : '…'} sub={t ? `Custom sales rung in POS, ${money(t.no_piece_sum, true)}` : ''} />
        <Tile label="$0 lines" tone={t && t.zero_lines ? 'amber' : 'plain'} value={t ? int(t.zero_lines) : '…'} sub={t && t.refunded ? `And ${int(t.refunded)} refunded ${t.refunded === 1 ? 'line' : 'lines'}` : 'Pieces given away on an order'} />
      </div>
      <div className="rp-filterbar">
        <div className="rp-filterbar__left">
          {([['all', `All (${t ? int(t.sold) : '…'})`], ['no_owner', `Owner missing (${t ? int(t.no_owner) : '…'})`], ['look', `Needs a look (${t ? int((t.no_piece + t.zero_lines + t.refunded)) : '…'})`], ['legacy', `No cost (${t ? int(t.legacy) : '…'})`]] as const).map(([k, l]) => (
            <button key={k} type="button" className={'rp-pill' + (show === k ? ' is-on' : '')} aria-pressed={show === k} onClick={() => setShow(k)}>{l}</button>
          ))}
        </div>
        <span className="rp-muted rp-count">{loading && !d ? '…' : `${int(rows.length)} lines, newest first`}</span>
      </div>
      <div className="rp-card">
        <div className="rp-table" role="table">
          <div className="rp-table__head" style={{ gridTemplateColumns: cols }} role="row">
            {['Piece', 'Paid · sale', 'Whose', 'Price', 'Cost', 'Profit', 'Check'].map((h, i) => <span key={h} role="columnheader" className={[3, 5].includes(i) ? 'r' : ''}>{h}</span>)}
          </div>
          {rows.map((l) => (
            <div key={l.line_id} className="rp-table__row" style={{ gridTemplateColumns: cols }} role="row">
              <span role="cell" className="rp-pcell" style={{ cursor: l.product_id ? 'pointer' : 'default' }} onClick={() => l.product_id && setOpen(l.product_id)}>
                <PhotoImg url={l.image_url} title={l.title} width={96} small />
                <span className="rp-pcell__text"><b>{l.no_piece ? (l.title && !/^custom sale$/i.test(l.title) ? l.title : 'Custom sale, no listing') : l.title}</b><span className="rp-sub">{l.no_piece ? `Custom item on ${l.source === 'counter' ? 'the sale' : `Shopify ${l.order_name ?? 'order'}`}, no listing` : l.brand}</span></span>
              </span>
              <span role="cell"><span className="rp-pcell__text"><b style={{ fontWeight: 600 }}>{dayShort(l.paid_at)}</b><span className="rp-sub">{saleCell(l)}</span></span></span>
              <span role="cell">{l.no_piece ? '' : <OwnerChip owner={l.owner} consignorKey={l.consignor_key} extra={l.owner === 'consignor' && l.split_pct != null ? `${n(l.split_pct)}%` : undefined} />}</span>
              <span role="cell" className="r">{money(l.price, true)}</span>
              <span role="cell">{costCell(l)}</span>
              <span role="cell" className="r">{l.profit != null ? <b>{money(l.profit, true)}</b> : l.fee != null ? <b>{money(l.fee, true)} fee</b> : <span className="rp-muted">unknown</span>}</span>
              <span role="cell" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {joinable(l) && <button type="button" className="rp-btn rp-btn--small rp-joinbtn" onClick={() => { setDone(''); setJoining(l) }}>{joinable(l)}</button>}
                {l.no_piece && !joinable(l) && <Badge tone="red">No piece</Badge>}
                {l.zero_line && !l.no_piece && <Badge tone="amber">$0 line</Badge>}
                {l.refunded != null && <Badge tone="grey">Refunded {money(l.refunded, true)}</Badge>}
                {!l.owner && !l.no_piece && (marking === l.line_id
                  ? <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      {OWNERS.map((o) => <AsyncButton key={o} small run={() => mark(l, o)}>{label(o)}</AsyncButton>)}
                      <select className="rp-select" style={{ height: 32, width: 130, fontSize: 12 }} value={consignorId} onChange={(e) => setConsignorId(e.target.value)} aria-label="Consignor">
                        <option value="">consignor…</option>
                        {(d?.consignors ?? []).map((c) => <option key={c.id} value={c.id}>{c.key} · {c.name}</option>)}
                      </select>
                    </span>
                  : <button type="button" className="rp-btn rp-btn--small" disabled={!signer} title={signer ? '' : 'Enter an owner ID (top right)'} onClick={() => setMarking(l.line_id)}>Mark owner</button>)}
              </span>
            </div>
          ))}
          {!loading && rows.length === 0 && <div className="rp-table__empty">Nothing here</div>}
        </div>
      </div>
      {open && <PieceDrawer productId={open} onClose={() => setOpen(null)} onChanged={refresh} />}
      {joining && <JoinSale part={joining} lines={d?.lines ?? []} signed={sg.signed} idRef={sg.idRef} onClose={() => setJoining(null)} onDone={(m) => { setJoining(null); setDone(m); refresh() }} />}
      {sg.askEl}
    </Screen>
  )
}
